export type LinkKind = "page" | "section" | "web" | "email" | "phone" | "whatsapp";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function containsControlCharacter(value: string): boolean {
  return [...value].some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });
}

export function inferLinkKind(value: string): LinkKind {
  const link = value.trim();
  if (/^mailto:/i.test(link)) return "email";
  if (/^tel:/i.test(link)) return "phone";
  if (/^(?:https?:\/\/)?(?:api\.)?wa\.me\//i.test(link) || /whatsapp\.com/i.test(link)) return "whatsapp";
  if (link.startsWith("#")) return "section";
  if (/^https?:\/\//i.test(link) || /^\/\//.test(link)) return "web";
  return "page";
}

export function normalizeLink(kind: LinkKind, rawValue: string): string {
  const raw = rawValue.trim();
  if (!raw) return "";
  if (kind === "section") return `#${raw.replace(/^#/, "").trim()}`;
  if (kind === "email") return `mailto:${raw.replace(/^mailto:/i, "").trim()}`;
  if (kind === "phone") {
    const phone = raw.replace(/^tel:/i, "").replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
    return `tel:${phone}`;
  }
  if (kind === "whatsapp") {
    if (/^https?:\/\/(?:api\.)?wa\.me\//i.test(raw) || /^https?:\/\/(?:www\.)?whatsapp\.com\//i.test(raw)) return raw;
    const phone = raw.replace(/\D/g, "");
    return `https://wa.me/${phone}`;
  }
  if (kind === "web") {
    if (/^\/\//.test(raw)) return `https:${raw}`;
    if (!/^[a-z][a-z\d+.-]*:/i.test(raw)) return `https://${raw}`;
  }
  return raw;
}

export function linkProblem(kind: LinkKind, rawValue: string): string {
  const normalized = normalizeLink(kind, rawValue);
  if (!normalized) return "Inserisci una destinazione.";
  if (containsControlCharacter(normalized)) return "Il collegamento contiene caratteri non validi.";
  if (/^(?:javascript|data|vbscript|file):/i.test(normalized)) return "Questo tipo di collegamento non è consentito.";
  if (/^[a-z][a-z\d+.-]*:/i.test(normalized) && !/^(?:https?|mailto|tel):/i.test(normalized))
    return "Questo tipo di collegamento non è supportato.";
  if (normalized.startsWith("//")) return "Inserisci l’indirizzo completo, incluso https://.";
  if (kind === "section" && !/^#[A-Za-z][\w:.-]*$/.test(normalized)) return "Inserisci l’ID della sezione, per esempio #contatti.";
  if (kind === "email" && !emailPattern.test(normalized.slice(7))) return "Inserisci un indirizzo email valido.";
  if (kind === "phone" && normalized.replace(/\D/g, "").length < 6) return "Inserisci un numero di telefono completo.";
  if (kind === "whatsapp" && normalized.replace(/\D/g, "").length < 8) return "Inserisci il numero WhatsApp con prefisso internazionale.";
  if (kind === "web") {
    try {
      const url = new URL(normalized);
      if (url.protocol !== "http:" && url.protocol !== "https:") return "Usa un indirizzo che inizi con http:// o https://.";
    } catch {
      return "Inserisci un indirizzo web valido.";
    }
  }
  return "";
}

export function linkInputValue(kind: LinkKind, value: string): string {
  if (kind === "email") return value.replace(/^mailto:/i, "");
  if (kind === "phone") return value.replace(/^tel:/i, "");
  if (kind === "section") return value.replace(/^#/, "");
  if (kind === "whatsapp" && /^https?:\/\/(?:api\.)?wa\.me\/\d+\/?$/i.test(value)) return value.replace(/^https?:\/\/(?:api\.)?wa\.me\//i, "").replace(/\/$/, "");
  return value;
}

export function linkPlaceholder(kind: LinkKind): string {
  if (kind === "page") return "/contatti oppure prenota.html";
  if (kind === "section") return "contatti";
  if (kind === "email") return "info@azienda.it";
  if (kind === "phone") return "+39 333 123 4567";
  if (kind === "whatsapp") return "393331234567";
  return "https://esempio.it/pagina";
}
